import dayjs from 'dayjs';
import { isNil } from 'lodash-es';

import { PivotSettings } from '@models/trial-attributes/trial-attributes';

import { MessagesConstants } from '@shared/constants/messages.constants';
import {
  ActionType,
  batchModifyPrepaidsMutation,
  ModifyPrepaidInput,
  PrepaidType,
  TrialImplementationStatus,
} from '@shared/services/gql.service';
import { decimalAdd } from '@shared/utils/floating-math';

import {
  AdjustmentsToPrepaidErrorRow,
  AdjustmentsToPrepaidRow,
} from './adjustments-to-prepaid.model';

const prepaidsMessages = MessagesConstants.PREPAIDS;

const ERROR_MAPPINGS = {
  accrual_period_closed_month: prepaidsMessages.ACCRUAL_PERIOD_IN_CLOSED_MONTH,
  amortize_both_blank: 'At least one of Short Term or Long Term must be filled for Amortize.',
  increase_both_blank: 'At least one of Short Term or Long Term must be filled for Increase.',
  increase_no_invoice: prepaidsMessages.INCREASE_MUST_HAVE_PAID_INVOICE,
  long_term_amortize: "Long Term can't be positive for Amortize.",
  long_term_increase: prepaidsMessages.INCREASE_POSITIVE,
  payment_date_closed_month: prepaidsMessages.PAYMENT_DATE_IN_CLOSED_MONTH,
  prepaid_type: 'Prepaid Type is required',
  short_term_amortize: "Short Term can't be positive for Amortize.",
  short_term_increase: prepaidsMessages.INCREASE_POSITIVE,
  total_impact_to_prepaid_amortize: prepaidsMessages.AMORTIZE_NEGATIVE,
  total_impact_to_prepaid_increase: prepaidsMessages.INCREASE_POSITIVE,
  total_impact_to_prepaid_reclass: prepaidsMessages.TOTAL_IMPACT_FOR_RECLASS,
} as const;

export const errorsToShowOverlay = (
  errorRows: Record<string, AdjustmentsToPrepaidErrorRow>,
  pivotSettings: PivotSettings
): string[] => {
  const uniqueMessages = new Set<string>();

  Object.values(errorRows).forEach((errorRow) => {
    errorRow.causes.forEach((cause) => {
      let message = ERROR_MAPPINGS[cause as keyof typeof ERROR_MAPPINGS] || '';

      if (cause === 'pivot_column') {
        const attrLabel = pivotSettings.primary?.attribute_name || 'Cost Category';
        const secondaryLabel = pivotSettings.secondary?.attribute_name;
        const fullLabel = secondaryLabel ? `${attrLabel} / ${secondaryLabel}` : attrLabel;

        message = `${fullLabel} is required`;
      }

      if (message) {
        uniqueMessages.add(message);
      }
    });
  });

  return Array.from(uniqueMessages);
};

export const getCauses = ({
  currentData,
  initialGridValues,
  openMonth,
  status,
}: {
  currentData: AdjustmentsToPrepaidRow;
  initialGridValues: AdjustmentsToPrepaidRow[];
  openMonth: string;
  status: TrialImplementationStatus | undefined;
}): AdjustmentsToPrepaidErrorRow['causes'] => {
  if (!currentData.prepaid_type) {
    return ['prepaid_type'];
  }

  const initialRowData = initialGridValues.find((row) => row.id === currentData.id);
  const causes: AdjustmentsToPrepaidErrorRow['causes'] = [];

  switch (currentData.prepaid_type) {
    case PrepaidType.PREPAID_TYPE_AMORTIZE:
      causes.push(...validateAmortizeType(currentData));
      break;
    case PrepaidType.PREPAID_TYPE_INCREASE:
      causes.push(...validateIncreaseType(currentData));
      break;
    case PrepaidType.PREPAID_TYPE_RECLASS:
      causes.push(...validateReclassType(currentData));
      break;
  }

  causes.push(...validateClosedMonths(currentData, initialRowData, openMonth, status));

  return causes;
};

export const mapInitialGridValue = (
  adjustment: batchModifyPrepaidsMutation,
  pivotSettings: PivotSettings
): AdjustmentsToPrepaidRow => {
  const isPivotSelected = !!pivotSettings.primary.attribute_name;
  const attributes = JSON.parse(adjustment.attributes || '[]') as {
    attribute_name: string;
    attribute_value: string;
  }[];

  let pivotColumn = [];

  if (isPivotSelected) {
    const primaryAttribute = attributes.find(
      (attribute) => attribute.attribute_name === pivotSettings.primary.attribute_name
    );
    const secondaryAttribute = attributes.find(
      (attribute) => attribute.attribute_name === pivotSettings.secondary.attribute_name
    );

    if (primaryAttribute) {
      pivotColumn.push(primaryAttribute.attribute_value);
    }

    if (secondaryAttribute) {
      pivotColumn.push(secondaryAttribute.attribute_value);
    }
  } else {
    pivotColumn = adjustment.amount_type ? [adjustment.amount_type] : [];
  }

  return {
    ...adjustment,
    accrual_period: adjustment.accrual_period
      ? dayjs(adjustment.accrual_period).format('YYYY-MM')
      : null,
    isNewAdjustment: false,
    pivot_column: pivotColumn,
    total_expense_per_invoice: adjustment.total_expense_per_invoice || 0,
    total_impact_to_prepaid: decimalAdd(
      adjustment.short_term_amount || 0,
      adjustment.long_term_amount || 0,
      2
    ),
  };
};

export const getAttributes = (
  row: AdjustmentsToPrepaidRow,
  pivotSettings: PivotSettings,
  isPivotSelected: boolean
) => {
  if (!isPivotSelected) return row.attributes;

  const attributes = [
    ...(row.pivot_column[0]
      ? [
          {
            attribute_name: pivotSettings.primary.attribute_name,
            attribute_value: row.pivot_column[0],
          },
        ]
      : []),
    ...(row.pivot_column[1]
      ? [
          {
            attribute_name: pivotSettings.secondary.attribute_name,
            attribute_value: row.pivot_column[1],
          },
        ]
      : []),
  ];

  return JSON.stringify(attributes);
};

export const mapToAdjustmentToSave = ({
  actionType,
  pivotSettings,
  reason,
  removedRowId,
  row,
  vendorId,
}: {
  actionType: ActionType;
  pivotSettings: PivotSettings;
  reason?: string;
  removedRowId: string;
  row: AdjustmentsToPrepaidRow;
  vendorId: string;
}): ModifyPrepaidInput => {
  if (actionType === ActionType.ACTION_TYPE_REMOVE) {
    return {
      accrual_period: null,
      action_type: ActionType.ACTION_TYPE_REMOVE,
      id: removedRowId,
      investigator_amount: null,
      invoice_date: null,
      invoice_id: null,
      invoice_total: null,
      long_term_amount: null,
      passthrough_amount: null,
      payment_date: null,
      prepaid_type: PrepaidType.PREPAID_TYPE_INCREASE,
      reason,
      services_amount: null,
      short_term_amount: null,
      vendor_id: vendorId,
    };
  }

  const isPivotSelected = !!pivotSettings.primary.attribute_name;

  return {
    accrual_period: row.accrual_period ? dayjs(row.accrual_period).format('YYYY-MM-DD') : null,
    action_type: actionType,
    amount_type: isPivotSelected ? row.amount_type : row.pivot_column[0] || '',
    attributes: getAttributes(row, pivotSettings, isPivotSelected),
    id: row.isNewAdjustment ? null : row.id,
    investigator_amount: row.investigator_amount || 0,
    invoice_date: row.invoice_date || null,
    invoice_id: row.invoice_id || null,
    invoice_total: row.invoice_total || 0,
    long_term_amount: row.long_term_amount || 0,
    passthrough_amount: row.passthrough_amount || 0,
    payment_date: row.payment_date || null,
    prepaid_type: row.prepaid_type || PrepaidType.PREPAID_TYPE_INCREASE,
    services_amount: row.services_amount || 0,
    short_term_amount: row.short_term_amount || 0,
    vendor_id: vendorId,
  };
};

// Helper functions
const validateIncreaseType = (
  currentData: AdjustmentsToPrepaidRow
): AdjustmentsToPrepaidErrorRow['causes'] => {
  const causes: AdjustmentsToPrepaidErrorRow['causes'] = [];

  // Both Short Term and Long Term are blank
  const shortTermBlank =
    isNil(currentData.short_term_amount) ||
    (typeof currentData.short_term_amount === 'string' && currentData.short_term_amount === '');
  const longTermBlank =
    isNil(currentData.long_term_amount) ||
    (typeof currentData.long_term_amount === 'string' && currentData.long_term_amount === '');

  const isShortTermZero = currentData.short_term_amount === 0;
  const isLongTermZero = currentData.long_term_amount === 0;

  if ((shortTermBlank || isShortTermZero) && (longTermBlank || isLongTermZero)) {
    causes.push('increase_both_blank');
  }

  if (currentData.invoice_total != null && currentData.invoice_total >= 0) {
    if (currentData.total_impact_to_prepaid && currentData.total_impact_to_prepaid < 0) {
      causes.push('total_impact_to_prepaid_increase');
    }

    if (currentData.short_term_amount && currentData.short_term_amount < 0) {
      causes.push('short_term_increase');
    }

    if (currentData.long_term_amount && currentData.long_term_amount < 0) {
      causes.push('long_term_increase');
    }
  }

  if (!currentData.pivot_column?.length) {
    causes.push('pivot_column');
  }

  if (!currentData.invoice_id) {
    causes.push('increase_no_invoice');
  }

  return causes;
};

const validateAmortizeType = (
  currentData: AdjustmentsToPrepaidRow
): AdjustmentsToPrepaidErrorRow['causes'] => {
  const causes: AdjustmentsToPrepaidErrorRow['causes'] = [];

  // Both Short Term and Long Term are blank
  const shortTermBlank =
    isNil(currentData.short_term_amount) ||
    (typeof currentData.short_term_amount === 'string' && currentData.short_term_amount === '');
  const longTermBlank =
    isNil(currentData.long_term_amount) ||
    (typeof currentData.long_term_amount === 'string' && currentData.long_term_amount === '');

  if (shortTermBlank && longTermBlank) {
    causes.push('amortize_both_blank');
  }

  // If Short Term is filled, it must be <= 0
  if (!shortTermBlank && Number(currentData.short_term_amount) > 0) {
    causes.push('short_term_amortize');
  }

  // If Long Term is filled, it must be <= 0
  if (!longTermBlank && Number(currentData.long_term_amount) > 0) {
    causes.push('long_term_amortize');
  }

  // If both are filled, total impact must be < 0
  if (!shortTermBlank || !longTermBlank) {
    const totalImpact = decimalAdd(
      Number(currentData.short_term_amount) || 0,
      Number(currentData.long_term_amount) || 0,
      2
    );

    if (totalImpact >= 0) {
      causes.push('total_impact_to_prepaid_amortize');
    }
  }

  if (!currentData.pivot_column?.length) {
    causes.push('pivot_column');
  }

  return causes;
};

const validateReclassType = (
  currentData: AdjustmentsToPrepaidRow
): AdjustmentsToPrepaidErrorRow['causes'] => {
  const causes: AdjustmentsToPrepaidErrorRow['causes'] = [];

  if (currentData.total_impact_to_prepaid && currentData.total_impact_to_prepaid !== 0) {
    causes.push('total_impact_to_prepaid_reclass');
  }

  return causes;
};

const validateClosedMonths = (
  currentData: AdjustmentsToPrepaidRow,
  initialRowData: AdjustmentsToPrepaidRow | undefined,
  openMonth: string,
  status: TrialImplementationStatus | undefined
): AdjustmentsToPrepaidErrorRow['causes'] => {
  const causes: AdjustmentsToPrepaidErrorRow['causes'] = [];

  if (
    status === TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED ||
    status === TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
  ) {
    if (currentData.accrual_period && dayjs(currentData.accrual_period).isBefore(openMonth)) {
      if (!initialRowData || initialRowData.accrual_period !== currentData.accrual_period) {
        causes.push('accrual_period_closed_month');
      }
    }

    if (currentData.payment_date && dayjs(currentData.payment_date).isBefore(openMonth)) {
      if (!initialRowData || initialRowData.payment_date !== currentData.payment_date) {
        causes.push('payment_date_closed_month');
      }
    }
  }

  return causes;
};
